
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, User, Award, Building, Check } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface SupplierResult {
  name: string;
  country?: string;
  count?: number;
  totalValue?: number;
  matchedField?: string;
  source?: string;
}

interface SupplierSearchProps {
  onSelectSupplier: (supplier: string) => void;
}

export const SupplierSearch = ({ onSelectSupplier }: SupplierSearchProps) => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SupplierResult[]>([]);
  const { toast } = useToast();
  const { t } = useTranslation('competitorAnalysis');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!query.trim()) return;
    
    setIsLoading(true);
    setResults([]);
    
    try {
      const { data, error } = await supabase.functions.invoke('search-suppliers', {
        body: { query: query.trim() }
      });
      
      if (error) {
        throw error;
      }
      
      if (data && Array.isArray(data) && data.length > 0) {
        setResults(data);
      } else {
        toast({
          title: "No suppliers found",
          description: "Try a different search term or check spelling",
          variant: "default"
        });
        setResults([]);
      }
    } catch (error) {
      console.error('Error searching suppliers:', error);
      toast({
        title: "Search failed",
        description: "An error occurred while searching. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSupplier = (supplier: string) => {
    onSelectSupplier(supplier);
    toast({
      title: "Supplier selected",
      description: `Analyzing tenders for "${supplier}"`,
      variant: "default"
    });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('search.title')}</CardTitle>
        <CardDescription>
          {t('search.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder={t('search.placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={isLoading || !query.trim()}>
              <Search className="h-4 w-4 mr-2" />
              {t('search.searchButton')}
            </Button>
          </div>
        </form>

        {isLoading && (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {!isLoading && results.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">
              {t('search.resultsCount', { count: results.length })}
            </h3>
            <div className="space-y-2">
              {results.map((supplier, index) => (
                <div 
                  key={index}
                  className="border p-3 rounded-md hover:bg-muted/50 cursor-pointer flex justify-between"
                  onClick={() => handleSelectSupplier(supplier.name)}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      {supplier.source === 'winner' ? (
                        <Award className="h-5 w-5 text-primary" />
                      ) : (
                        <Building className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium">{supplier.name}</h4>
                      <div className="text-sm text-muted-foreground">
                        {supplier.country && <span className="mr-2">{t('search.country')}: {supplier.country}</span>}
                        {supplier.count && <span>{t('search.contracts')}: {supplier.count}</span>}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-2">
                    <Check className="h-4 w-4 mr-1" />
                    {t('search.selectButton')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
